import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import { wrapPoint, distanceM, bearingDeg, formatDistance, formatLatLng, pathDistanceM, M_PER, UNIT_LABEL } from "../helpers/geo";
import { copyText } from "../helpers/clipboard";

const read = (key, allowed, fallback) => {
  try { const v = localStorage.getItem(key); return allowed.includes(v) ? v : fallback; } catch { return fallback; }
};
const save = (key, v) => { try { localStorage.setItem(key, v); } catch {} };

export const MAX_RADIUS_M = 5000 * 1000;

// All state + actions for the map tools (measure, coordinates, go-to, radius, scale bar, fit, fullscreen).
export default function useMapTools({ ships, setMessage, setFlyPosition }) {
  const mapRef = useRef(null);        // the Leaflet map (set by <MapBridge/>)
  const containerRef = useRef(null);  // .map-container, used for fullscreen
  const coordsOutRef = useRef(null);  // <span> the live cursor readout writes into

  const [unit, setUnitState] = useState(() => read("open-wrecks:unit", ["km", "nm", "mi"], "km"));
  const [coordsFormat, setCoordsFormatState] = useState(() => read("open-wrecks:coords", ["dd", "dms"], "dd"));
  const setUnit = (u) => { setUnitState(u); save("open-wrecks:unit", u); };
  const setCoordsFormat = (f) => { setCoordsFormatState(f); save("open-wrecks:coords", f); };

  const [showCoordinates, setShowCoordinates] = useState(false);
  const [pin, setPin] = useState(null); // [lat, lng] dropped by a click or "Go to"

  const [measureMode, setMeasureMode] = useState(false);
  const [measurePoints, setMeasurePoints] = useState([]);

  const [radiusMode, setRadiusMode] = useState(false);
  const [radiusCenter, setRadiusCenter] = useState(null);
  const [radiusValue, setRadiusValue] = useState("100");

  const [gotoOpen, setGotoOpen] = useState(false);
  const [showScale, setShowScale] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);

  /* ----- measuring ----- */
  const toggleMeasure = () => {
    setMeasureMode((m) => !m);
    setRadiusMode(false);
  };
  const addMeasurePoint = useCallback((p) => {
    const pt = wrapPoint(p);
    setMeasurePoints((list) => {
      const last = list[list.length - 1];
      return last && last[0] === pt[0] && last[1] === pt[1] ? list : [...list, pt]; // ignore double-clicks on one spot
    });
  }, []);
  const moveMeasurePoint = (i, p) => setMeasurePoints((list) => list.map((x, j) => (j === i ? wrapPoint(p) : x)));
  const removeMeasurePoint = (i) => setMeasurePoints((list) => list.filter((_, j) => j !== i));
  const undoMeasure = () => setMeasurePoints((list) => list.slice(0, -1));
  const clearMeasure = () => setMeasurePoints([]);

  const copyMeasurement = async () => {
    if (measurePoints.length < 2) return;
    const lines = measurePoints.slice(1).map((p, i) => {
      const a = measurePoints[i];
      return `${i + 1}→${i + 2}: ${formatDistance(distanceM(a, p), unit)} (bearing ${Math.round(bearingDeg(a, p))}°)`;
    });
    lines.push(`Total: ${formatDistance(pathDistanceM(measurePoints), unit)} (WGS-84 ellipsoid)`);
    setMessage((await copyText(lines.join("\n"))) ? "Measurement copied." : "Couldn't copy to the clipboard.");
  };

  /* ----- coordinates ----- */
  const toggleCoordinates = () => setShowCoordinates((v) => !v);
  const copyCoordinates = async () => {
    const text = pin ? formatLatLng(pin[0], pin[1], coordsFormat) : coordsOutRef.current?.textContent;
    if (!text || !/\d/.test(text)) return setMessage("Move over the map or tap it to pick a point first.");
    setMessage((await copyText(text)) ? `Copied ${text}` : "Couldn't copy to the clipboard.");
  };
  const copyLatLng = async (lat, lng) => {
    const text = formatLatLng(lat, lng, coordsFormat);
    setMessage((await copyText(text)) ? `Copied ${text}` : "Couldn't copy to the clipboard.");
  };

  const goTo = (lat, lng) => {
    setPin([lat, lng]);
    setFlyPosition([lat, lng]);
  };

  /* ----- radius search ----- */
  const toggleRadius = () => {
    setRadiusMode((m) => !m);
    setMeasureMode(false);
  };
  const radiusM = Math.min(MAX_RADIUS_M, Math.max(0, (parseFloat(radiusValue) || 0) * M_PER[unit]));
  const radiusTooBig = (parseFloat(radiusValue) || 0) * M_PER[unit] > MAX_RADIUS_M;
  const closeRadius = () => { setRadiusMode(false); setRadiusCenter(null); };

  const radiusResults = useMemo(() => {
    if (!radiusCenter || !radiusM) return [];
    return ships
      .map((s) => ({ ship: s, d: distanceM(radiusCenter, [s.lat, s.lng]) }))
      .filter((r) => r.d <= radiusM)
      .sort((a, b) => a.d - b.d)
      .map((r) => ({ ...r, bearing: bearingDeg(radiusCenter, [r.ship.lat, r.ship.lng]) }));
  }, [ships, radiusCenter, radiusM]);

  const fitCircle = useCallback((center, meters) => {
    const map = mapRef.current;
    if (!map || !center || !meters) return;
    map.fitBounds(L.circle(center, { radius: meters }).getBounds(), { padding: [40, 40] });
  }, []);

  const pickRadiusCenter = (p) => {
    const c = wrapPoint(p);
    setRadiusCenter(c);
    fitCircle(c, radiusM);
  };

  const radiusFromMyLocation = () => {
    if (!navigator.geolocation) return setMessage("Geolocation is not supported by your browser.");
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => pickRadiusCenter([coords.latitude, coords.longitude]),
      (e) => setMessage("Unable to retrieve your location: " + e.message)
    );
  };

  /* ----- view ----- */
  const fitAll = () => {
    const map = mapRef.current;
    if (!map || !ships.length) return setMessage("No wrecks to show yet.");
    map.fitBounds(L.latLngBounds(ships.map((s) => [s.lat, s.lng])), { padding: [50, 50], maxZoom: 8 });
  };

  const toggleFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement) document.exitFullscreen?.();
    else if (el.requestFullscreen) el.requestFullscreen().catch(() => setMessage("Your browser blocked fullscreen."));
    else setMessage("Fullscreen isn't supported by this browser.");
  };

  useEffect(() => {
    const onChange = () => {
      setFullscreen(document.fullscreenElement === containerRef.current);
      setTimeout(() => mapRef.current?.invalidateSize(), 100);
    };
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  return {
    mapRef, containerRef, coordsOutRef,
    unit, setUnit, unitLabel: UNIT_LABEL[unit], coordsFormat, setCoordsFormat,
    showCoordinates, toggleCoordinates, pin, setPin, copyCoordinates, copyLatLng,
    measureMode, setMeasureMode, measurePoints, toggleMeasure, addMeasurePoint, moveMeasurePoint, removeMeasurePoint, undoMeasure, clearMeasure, copyMeasurement,
    radiusMode, radiusCenter, radiusValue, setRadiusValue, radiusM, radiusTooBig, radiusResults, toggleRadius, closeRadius, pickRadiusCenter, radiusFromMyLocation, fitCircle,
    gotoOpen, setGotoOpen, goTo,
    showScale, setShowScale, fullscreen, toggleFullscreen, fitAll,
  };
}
